'use client';

import { cva, VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, ReactNode, useMemo } from 'react';
import { AddressIcon } from '@/features/address/ui/address-icon';
import { AddressIconOption } from '@/features/address/ui/address-icon/types';
import { useAddressFormatter } from '@/shared/lib/hooks/useAddressFormater';
import { Address, UserAddressType } from '@/shared/model/Address';
import './address-card.scss';

const addressCardVariants = cva('address-card', {
    variants: {
        variant: {
            default: null,
            outlined: 'address-card--outlined',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type Props = Omit<ComponentPropsWithoutRef<'div'>, 'children'> &
    VariantProps<typeof addressCardVariants> & {
        name?: string;
        address: Address;
        type?: UserAddressType;
        right?: ReactNode;
        icon: AddressIconOption;
    };
export const AddressCard = ({ name, address, type, right, className, variant, icon, ...props }: Props) => {
    const t = useTranslations();
    const formattedAddress = useAddressFormatter(address);
    const selectedName = useMemo(() => {
        if (name) {
            return name;
        }
        return type ? t(`ADDRESS.TYPE.${type}`) : '';
    }, [t, name, type]);

    return (
        <div className={addressCardVariants({ variant, className })} {...props}>
            <div className="address-card__info" data-testid="address-card-info">
                <AddressIcon icon={icon} data-testid="address-card-info-icon" data-value={icon} />
                <div className="address-card__text">
                    <span data-testid="address-card-info-name">{selectedName}</span>
                    <span data-testid="address-card-info-address">{formattedAddress}</span>
                </div>
            </div>
            {right && <div className="address-card__right-container">{right}</div>}
        </div>
    );
};
